/**
 * Bundler Cart Integration for Horizon Theme
 * 
 * This script integrates the 3rd party bundler app with the theme's cart drawer,
 * providing seamless add-to-cart functionality without page refreshes.
 * 
 * Features:
 * - Opens cart drawer after bundle addition
 * - Updates cart icon count in real-time
 * - Refreshes cart drawer contents
 * - Handles error scenarios gracefully
 */

import { CartAddEvent, CartUpdateEvent } from '@theme/events';

class BundlerCartIntegration {
  constructor() {
    this.isInitialized = false;
    this.debugMode = false; // Set to true for console logging during development
    
    // Bind methods to preserve context
    this.handleBundlerLoaded = this.handleBundlerLoaded.bind(this);
    this.handleBundleAddedToCart = this.handleBundleAddedToCart.bind(this);
    this.handleProductAdded = this.handleProductAdded.bind(this);
    
    this.init();
  }

  /**
   * Initialize the integration
   */
  init() {
    this.log('🚀 Initializing Bundler Cart Integration...');
    
    // Check if bundler is already loaded
    if (typeof window['bndlr'] !== 'undefined') {
      this.handleBundlerLoaded();
    } else {
      // Wait for bundler to load
      document.addEventListener('bundler:loaded', this.handleBundlerLoaded);
    }
  }

  /**
   * Handle bundler app loaded event
   */
  handleBundlerLoaded() {
    if (this.isInitialized) return;
    
    this.log('✅ Bundler app loaded, setting up integration...');
    
    // Prevent bundler's default checkout behavior for cart drawer integration
    if (typeof window.bndlr.preventBundlerCheckout === 'function') {
      window.bndlr.preventBundlerCheckout();
      this.log('🚫 Bundler default checkout prevented');
    }
    
    // Set up event listeners
    this.setupEventListeners();
    
    this.isInitialized = true;
    this.log('🎉 Bundler Cart Integration initialized successfully!');
  }

  /**
   * Set up event listeners for bundler events
   */
  setupEventListeners() {
    // Primary event: Complete bundle added to cart
    document.addEventListener('bndlr:bundle_added_to_cart', this.handleBundleAddedToCart);
    
    // Fallback event: Individual product added (fires multiple times for bundles)
    document.addEventListener('product:added', this.handleProductAdded);
    
    this.log('👂 Event listeners set up for bundler events');
  }

  /**
   * Handle bundle added to cart event (primary integration point)
   */
  async handleBundleAddedToCart(event) {
    this.log('🛒 Bundle added to cart event received', event.detail);
    
    try {
      // Add small delay to ensure cart is fully updated on server
      await this.delay(100);
      
      // Fetch fresh cart data
      const cartData = await this.fetchCartData();
      
      // Get section data for cart refresh
      const sectionsData = await this.fetchCartSections();
      
      // Dispatch theme cart event to trigger all cart updates
      this.dispatchThemeCartEvent(cartData, sectionsData, 'bundler-app');
      
      // Open cart drawer
      this.openCartDrawer();
      
      // Show success feedback
      this.showSuccessFeedback();
      
      this.log('✅ Bundle cart integration completed successfully');
      
    } catch (error) {
      this.log('❌ Error in bundle cart integration:', error);
      this.handleIntegrationError(error);
    }
  }

  /**
   * Handle individual product added event (fallback)
   */
  handleProductAdded(event) {
    // Only use this as fallback if bundle event doesn't fire
    // Add debouncing to avoid multiple triggers for bundle items
    if (!this.productAddedTimeout) {
      this.productAddedTimeout = setTimeout(() => {
        this.log('📦 Product added event (fallback)', event.detail);
        // Use same logic as bundle event but with different source
        this.handleBundleAddedToCart(event);
        this.productAddedTimeout = null;
      }, 500); // Wait 500ms to see if bundle event fires
    }
  }

  /**
   * Fetch current cart data from Shopify
   */
  async fetchCartData() {
    const response = await fetch('/cart.js', {
      method: 'GET',
      headers: {
        'Accept': 'application/json',
        'X-Requested-With': 'XMLHttpRequest'
      }
    });

    if (!response.ok) {
      throw new Error(`Failed to fetch cart data: ${response.status}`);
    }

    return await response.json();
  }

  /**
   * Fetch cart sections for theme refresh
   */
  async fetchCartSections() {
    // Get all cart-related section IDs
    const cartSectionIds = this.getCartSectionIds();
    
    if (cartSectionIds.length === 0) {
      return {};
    }

    const sectionsParam = cartSectionIds.join(',');
    const response = await fetch(`/?sections=${sectionsParam}`, {
      method: 'GET',
      headers: {
        'Accept': 'application/json',
        'X-Requested-With': 'XMLHttpRequest'
      }
    });

    if (!response.ok) {
      this.log('⚠️ Could not fetch sections, cart will refresh without section data');
      return {};
    }

    return await response.json();
  }

  /**
   * Get cart-related section IDs from the page
   */
  getCartSectionIds() {
    const sectionIds = [];
    
    // Find cart items components
    const cartItemsComponents = document.querySelectorAll('cart-items-component[data-section-id]');
    cartItemsComponents.forEach(component => {
      const sectionId = component.dataset.sectionId;
      if (sectionId && !sectionIds.includes(sectionId)) {
        sectionIds.push(sectionId);
      }
    });
    
    // Find other cart-related sections
    const cartSections = document.querySelectorAll('[data-section-id*="cart"], [data-section-id*="drawer"]');
    cartSections.forEach(section => {
      const sectionId = section.dataset.sectionId;
      if (sectionId && !sectionIds.includes(sectionId)) {
        sectionIds.push(sectionId);
      }
    });
    
    return sectionIds;
  }

  /**
   * Dispatch theme-compatible cart event
   */
  dispatchThemeCartEvent(cartData, sectionsData, source = 'bundler-app') {
    // Create CartAddEvent compatible with theme
    const cartAddEvent = new CartAddEvent(
      cartData, // resource: full cart object
      'bundler-integration', // sourceId
      {
        itemCount: cartData.item_count,
        source: source,
        sections: sectionsData,
        didError: false
      }
    );

    // Dispatch the event
    document.dispatchEvent(cartAddEvent);
    
    this.log('📡 Theme cart event dispatched', {
      itemCount: cartData.item_count,
      source: source,
      sectionsCount: Object.keys(sectionsData).length
    });
  }

  /**
   * Open the cart drawer
   */
  openCartDrawer() {
    const cartDrawer = document.querySelector('cart-drawer-component');
    
    if (!cartDrawer) {
      this.log('⚠️ Cart drawer component not found');
      return;
    }

    // Try different methods to open the drawer
    if (typeof cartDrawer.open === 'function') {
      cartDrawer.open();
      this.log('🗂️ Cart drawer opened via open() method');
    } else if (typeof cartDrawer.showDialog === 'function') {
      cartDrawer.showDialog();
      this.log('🗂️ Cart drawer opened via showDialog() method');
    } else {
      // Fallback: trigger click on cart button
      const cartButton = cartDrawer.querySelector('button[on\\:click="/open"]');
      if (cartButton) {
        cartButton.click();
        this.log('🗂️ Cart drawer opened via button click');
      } else {
        this.log('❌ Could not open cart drawer - no available method');
      }
    }
  }

  /**
   * Show success feedback to user
   */
  showSuccessFeedback() {
    // Create temporary success message
    const successMessage = document.createElement('div');
    successMessage.className = 'bundler-success-message';
    successMessage.innerHTML = `
      <div style="
        position: fixed;
        top: 20px;
        right: 20px;
        background: #4CAF50;
        color: white;
        padding: 12px 20px;
        border-radius: 4px;
        z-index: 10000;
        font-size: 14px;
        box-shadow: 0 2px 10px rgba(0,0,0,0.1);
        animation: bundlerSlideIn 0.3s ease-out;
      ">
        ✅ Bundle added to cart!
      </div>
    `;

    // Add animation styles
    if (!document.querySelector('#bundler-success-styles')) {
      const styles = document.createElement('style');
      styles.id = 'bundler-success-styles';
      styles.textContent = `
        @keyframes bundlerSlideIn {
          from { transform: translateX(100%); opacity: 0; }
          to { transform: translateX(0); opacity: 1; }
        }
        @keyframes bundlerSlideOut {
          from { transform: translateX(0); opacity: 1; }
          to { transform: translateX(100%); opacity: 0; }
        }
      `;
      document.head.appendChild(styles);
    }

    document.body.appendChild(successMessage);

    // Remove message after 3 seconds
    setTimeout(() => {
      successMessage.style.animation = 'bundlerSlideOut 0.3s ease-in';
      setTimeout(() => {
        if (successMessage.parentNode) {
          successMessage.parentNode.removeChild(successMessage);
        }
      }, 300);
    }, 3000);
  }

  /**
   * Handle integration errors
   */
  handleIntegrationError(error) {
    this.log('❌ Integration error:', error);
    
    // Show error message to user
    const errorMessage = document.createElement('div');
    errorMessage.innerHTML = `
      <div style="
        position: fixed;
        top: 20px;
        right: 20px;
        background: #f44336;
        color: white;
        padding: 12px 20px;
        border-radius: 4px;
        z-index: 10000;
        font-size: 14px;
        box-shadow: 0 2px 10px rgba(0,0,0,0.1);
      ">
        ⚠️ Cart update failed. Please refresh the page.
      </div>
    `;

    document.body.appendChild(errorMessage);

    setTimeout(() => {
      if (errorMessage.parentNode) {
        errorMessage.parentNode.removeChild(errorMessage);
      }
    }, 5000);
  }

  /**
   * Utility: Add delay
   */
  delay(ms) {
    return new Promise(resolve => setTimeout(resolve, ms));
  }

  /**
   * Utility: Debug logging
   */
  log(message, data = null) {
    if (this.debugMode) {
      if (data) {
        console.log(`[Bundler Integration] ${message}`, data);
      } else {
        console.log(`[Bundler Integration] ${message}`);
      }
    }
  }

  /**
   * Enable debug mode
   */
  enableDebug() {
    this.debugMode = true;
    this.log('🐛 Debug mode enabled');
  }

  /**
   * Disable debug mode
   */
  disableDebug() {
    this.debugMode = false;
  }
}

// Initialize the integration when DOM is ready
if (document.readyState === 'loading') {
  document.addEventListener('DOMContentLoaded', () => {
    window.bundlerCartIntegration = new BundlerCartIntegration();
  });
} else {
  window.bundlerCartIntegration = new BundlerCartIntegration();
}

// Export for potential external use
export default BundlerCartIntegration; 